import {
  Box,
  Button,
  FormattedRelativeTime,
  Icon,
  Paragraph,
  Strong,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';

const CLEAR_HELP =
  'Clearing your connection allows you to reconnect to Segment from this page. Do this if a Segment Source or Workspace has been changed or deleted from within Segment.';

type SegmentConnectionSummaryProps = {
  /** Unix seconds from the stored record, or null when nothing has verified it. */
  lastVerifiedAt: number | null;
  /** `workspace/source`, already parsed from the API's full path. */
  connectedSource: string | null;
  onOpenClearModal: () => void;
};

/**
 * When the connection was last confirmed, and the way out when it cannot be.
 *
 * Shown only for a workspace that has a stored connection with a verification
 * time: before either exists there is nothing to report, and the panel above is
 * offering to create the connection instead.
 */
export function SegmentConnectionSummary({
  lastVerifiedAt,
  connectedSource,
  onOpenClearModal,
}: SegmentConnectionSummaryProps) {
  if (lastVerifiedAt == null || connectedSource == null) return null;

  return (
    <>
      <Paragraph
        sizeVariant="xs"
        variant="secondary"
        mt="100"
        mb="0"
        textAlign="center"
        data-test-segment-in-last-verified
      >
        Last verified <FormattedRelativeTime date={lastVerifiedAt * 1000} /> to Segment Source{' '}
        <Strong>{connectedSource}</Strong>
      </Paragraph>

      <Box textAlign="center" mt="300" data-test-segment-in-clear-connection-info>
        <Paragraph m="0">Something not working?</Paragraph>
        <div>
          <Button variant="tertiary" size="sm" onClick={onOpenClearModal} data-test-segment-in-open-clear-modal>
            {/* oxlint-disable-next-line local-rules/content-length -- migrated copy, kept exact (FPS-2721): it answers the "Something not working?" line above, and the tentative verb marks it as a remedy to try rather than an action to take */}
            Try clearing the connection
          </Button>
          <Tooltip>
            <Icon name="help" />
            <TooltipContent>
              <Paragraph color="inherit" m="0">
                {CLEAR_HELP}
              </Paragraph>
            </TooltipContent>
          </Tooltip>
        </div>
      </Box>
    </>
  );
}
